import type { OverlayGeometry } from "./editorRelativeRect.ts";
import type { Rect, ResizeHandle } from "./resizeDelta.ts";

/** Default thickness of edge handles, in CSS pixels. */
export const HANDLE_THICKNESS = 8;

/** Default size of the corner handles' interactive square, in CSS pixels. */
export const CORNER_SIZE = 10;

export interface HandleGeometry {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface HandlePositionConfig {
  handle: ResizeHandle;
  cursor: string;
  geometry: HandleGeometry;
}

export interface HandlePositionOptions {
  thickness?: number;
  cornerSize?: number;
  /** Rotation of the element in radians (clockwise). Used to rotate cursors. */
  rotation?: number;
}

/**
 * Each handle's angular position around the rect, in 45° steps clockwise from
 * the top edge. Adding the element's rotation (also in 45° steps) and taking
 * mod 4 picks the right axis-aligned resize cursor.
 */
const HANDLE_STEP: Record<ResizeHandle, number> = {
  top: 0,
  "top-right": 1,
  right: 2,
  "bottom-right": 3,
  bottom: 4,
  "bottom-left": 5,
  left: 6,
  "top-left": 7,
};

const CURSORS_BY_STEP = ["ns-resize", "nesw-resize", "ew-resize", "nwse-resize"] as const;

const resolveCursor = (handle: ResizeHandle, rotation: number): string => {
  const rotationStep = Math.round((rotation * 4) / Math.PI);
  const step = (((HANDLE_STEP[handle] + rotationStep) % 8) + 8) % 8;
  return CURSORS_BY_STEP[step % 4];
};

/**
 * Returns position + size for the eight resize handles around `rect`. Edge
 * handles span between the corner squares; corners overlap the edge bands by
 * half the thickness so cursors meet exactly at the corner pixel.
 */
export const getResizeHandlePositions = (rect: Rect, options: HandlePositionOptions = {}): HandlePositionConfig[] => {
  const thickness = options.thickness ?? HANDLE_THICKNESS;
  const cornerSize = options.cornerSize ?? CORNER_SIZE;
  const rotation = options.rotation ?? 0;
  const halfThickness = thickness / 2;
  const cornerSquare = cornerSize + halfThickness;
  const { top, left, width, height } = rect;

  return [
    {
      handle: "top",
      cursor: resolveCursor("top", rotation),
      geometry: {
        top: top - halfThickness,
        left: left + cornerSize,
        width: width - cornerSize * 2,
        height: thickness,
      },
    },
    {
      handle: "right",
      cursor: resolveCursor("right", rotation),
      geometry: {
        top: top + cornerSize,
        left: left + width - halfThickness,
        width: thickness,
        height: height - cornerSize * 2,
      },
    },
    {
      handle: "bottom",
      cursor: resolveCursor("bottom", rotation),
      geometry: {
        top: top + height - halfThickness,
        left: left + cornerSize,
        width: width - cornerSize * 2,
        height: thickness,
      },
    },
    {
      handle: "left",
      cursor: resolveCursor("left", rotation),
      geometry: {
        top: top + cornerSize,
        left: left - halfThickness,
        width: thickness,
        height: height - cornerSize * 2,
      },
    },
    {
      handle: "top-left",
      cursor: resolveCursor("top-left", rotation),
      geometry: {
        top: top - halfThickness,
        left: left - halfThickness,
        width: cornerSquare,
        height: cornerSquare,
      },
    },
    {
      handle: "top-right",
      cursor: resolveCursor("top-right", rotation),
      geometry: {
        top: top - halfThickness,
        left: left + width - cornerSize,
        width: cornerSquare,
        height: cornerSquare,
      },
    },
    {
      handle: "bottom-right",
      cursor: resolveCursor("bottom-right", rotation),
      geometry: {
        top: top + height - cornerSize,
        left: left + width - cornerSize,
        width: cornerSquare,
        height: cornerSquare,
      },
    },
    {
      handle: "bottom-left",
      cursor: resolveCursor("bottom-left", rotation),
      geometry: {
        top: top + height - cornerSize,
        left: left - halfThickness,
        width: cornerSquare,
        height: cornerSquare,
      },
    },
  ];
};

/**
 * Returns resize-handle positions for an `OverlayGeometry`. Handles sit at
 * (0, 0) inside a container that carries the geometry's transform; thickness
 * and corner size are counter-scaled by the geometry's scale magnitude so the
 * rendered hit zones stay constant size regardless of canvas zoom or element
 * scale.
 */
export const getOverlayResizeHandles = (
  geometry: OverlayGeometry,
  options: HandlePositionOptions = {},
): HandlePositionConfig[] => {
  const scale = geometry.scaleX || 1;
  return getResizeHandlePositions(
    { top: 0, left: 0, width: geometry.intrinsicRect.width, height: geometry.intrinsicRect.height },
    {
      thickness: (options.thickness ?? HANDLE_THICKNESS) / scale,
      cornerSize: (options.cornerSize ?? CORNER_SIZE) / scale,
      rotation: geometry.rotation,
    },
  );
};
